// ============================================================================
// AI ASSISTANT ACTIONS
// ============================================================================

import { isEntityRegistryKey, resolveEntityDisplayName } from "@/lib/entity-registry";
import type { AskMeridianModalData } from "@/lib/modals/modal-data/admin";
import type { BaseEntity } from "../../UniversalCRMTable/types";
import type { DetailActionContext } from "../types/detailPage";
import type { UniversalActionHandlers } from "./types";

/**
 * "Ask Meridian" action — opens a Meridian chat scoped to this specific entity.
 *
 * Passes the row's record straight through to the modal so the focused chat has
 * the entity's context without needing a query-string parameter. The entity key
 * is validated against the registry (strong key, not a free string) and the
 * display name is resolved the same way the detail header does, so the row and
 * detail entry points always show the same name.
 */
const askMeridian = async <T extends BaseEntity>(
  item: T,
  context: DetailActionContext<T>
): Promise<void> => {
  const entityName = context.pageConfig?.entityName ?? context.entityName;

  if (!isEntityRegistryKey(entityName)) {
    context.onToast(
      "Couldn't open Meridian for this item — unknown entity type.",
      "error"
    );
    return;
  }

  const modalData: AskMeridianModalData = {
    entityType: entityName,
    entityId: item.id,
    entityData: item,
    entityDisplayName: resolveEntityDisplayName(entityName, item),
  };

  context.onOpenModal("ask-meridian-entity", modalData);
};

/**
 * AI Assistant action handlers
 */
export const aiAssistantActions: UniversalActionHandlers = {
  askMeridian,
};
